import React, { useEffect, useMemo, useState } from "react";
import Footer from "./Footer";
import Header from "./Header";
import axios from "axios";
import config from "../../config";

const MAX_CONDITIONS = 7;
const MAX_COURIERS = 5;

const CONDITION_TYPES = [
  ["payment", "Payment Mode"],
  ["weight", "Weight"],
  ["orderValue", "Order Value"],
  ["pincode", "Pincode"],
  ["zone", "Zone Wise"],
  ["state", "State"],
  ["sku", "Product SKU"],
];

const ZONES = [
  "Local Zone",
  "State Zone ",
  "Metro To Metro Zone",
  "ROI Zone",
  "Special Zone",
];

const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Jammu and Kashmir",
  "Ladakh",
];

const COURIERS = [
  {
    id: "shadowfax_nextday",
    carrier: "Shadowfax",
    service: "Next Day",
    badge: "S",
    cls: "dapi-cl-sf",
    services: ["Next Day"],
  },
  {
    id: "delhivery_surface",
    carrier: "Delhivery",
    service: "Surface",
    badge: "D",
    cls: "dapi-cl-d",
    services: ["Surface"],
  },
  {
    id: "delhivery_express",
    carrier: "Delhivery",
    service: "Express",
    badge: "D",
    cls: "dapi-cl-d",
    services: ["Express"],
  },
  {
    id: "xpressbees_standard",
    carrier: "XpressBees",
    service: "Standard",
    badge: "X",
    cls: "dapi-cl-xb",
    services: ["Standard"],
  },
  {
    id: "xpressbees_priority",
    carrier: "XpressBees",
    service: "Priority",
    badge: "X",
    cls: "dapi-cl-xb",
    services: ["Priority"],
  },
  {
    id: "dtdc_express",
    carrier: "DTDC",
    service: "Express",
    badge: "D",
    cls: "dapi-cl-dtdc",
    services: ["Express"],
  },
  {
    id: "dtdc_twoway",
    carrier: "DTDC",
    service: "Two-way",
    badge: "D",
    cls: "dapi-cl-dtdc",
    services: ["Two-way"],
  },
  {
    id: "bluedart_plus",
    carrier: "Blue Dart",
    service: "Plus",
    badge: "B",
    cls: "dapi-cl-bd",
    services: ["Plus"],
  },
  {
    id: "bluedart_apex",
    carrier: "Blue Dart",
    service: "Apex",
    badge: "B",
    cls: "dapi-cl-bd",
    services: ["Apex"],
  },
];

const uid = (p = "id") =>
  `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const emptyCondition = (type = "payment") => ({
  id: uid("cond"),
  type,
  payment: "Prepaid",
  wtOp: "lte",
  wtFrom: "",
  wtTo: "",
  wtVal: "",
  wtUnit: "KG",
  valOp: "lte",
  valFrom: "",
  valTo: "",
  valVal: "",
  valCur: "INR",
  pickupFile: null,
  deliveryFile: null,
  zones: [],
  fromState: "",
  toState: "",
  skuFile: null,
});

const defaultRule = () => ({
  name: "Untitled Rule",
  type: "Domestic",
  conditions: [],
  action: {
    mode: "custom",
    couriers: [],
  },
});

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("ship_rocket_user") || "{}");
  } catch {
    return {};
  }
};

const normalizeCondition = (c) => ({
  ...emptyCondition(c.type),
  ...c,
  id: c.id || c.condition_id || uid("cond"),
});

const Shipping_rule_edit = () => {
  const userData = useMemo(getUser, []);
  const params = new URLSearchParams(window.location.search);
  const editId = params.get("id");
  const isEdit = Boolean(editId && editId !== "new");

  const [rule, setRule] = useState(defaultRule());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dragId, setDragId] = useState(null);

  const [courierPartners, setCourierPartners] = useState([]);
  const [loadingCourierPartners, setLoadingCourierPartners] = useState(false);

  useEffect(() => {
    if (isEdit) loadRule();
  }, [editId]);

  useEffect(() => {
    getCourierPartners();
  }, []);

  const getCourierPartners = async () => {
    try {
      setLoadingCourierPartners(true);

      const response = await axios.post(
        `${config.apiUrl}/shipment/all_product_list`,
        {
          mode: "Domestic",
          order_type: "Forward",
        },
        {
          headers: config.headers,
        }
      );

      if (
        response.data?.status === true ||
        response.data?.status === "success"
      ) {
        const output = Array.isArray(response.data.output)
          ? response.data.output
          : [];

        /*
         * Product is our selectable shipping option.
         *
         * product_id is stored internally.
         * product_name is displayed to the user.
         */
        const unique = output.filter((item, index, array) => {
          const key = item?.product_id || item?.product_name;

          return (
            key &&
            index ===
              array.findIndex((x) => {
                const otherKey = x?.product_id || x?.product_name;

                return otherKey === key;
              })
          );
        });

        setCourierPartners(unique);
      } else {
        setCourierPartners([]);
      }
    } catch (error) {
      console.error("all_product_list error =>", error);

      setCourierPartners([]);
    } finally {
      setLoadingCourierPartners(false);
    }
  };

  const loadRule = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        config.apiUrl + "/shipping_rule/detail",
        { customer_id: userData.customer_id, rule_id: editId },
        { headers: config.headers }
      );
      if (!res.data?.status) {
        setError(res.data?.message || "Unable to load rule");
        return;
      }
      const d = res.data.output || {};
      setRule({
        rule_id: d.rule_id,
        name: d.name || "Untitled Rule",
        type: d.type || "Domestic",
        conditions: Array.isArray(d.conditions)
          ? d.conditions.map(normalizeCondition)
          : [],
        action: {
          mode: ["custom", "fastest", "cheapest"].includes(d.action?.mode)
            ? d.action.mode
            : "custom",
          couriers: Array.isArray(d.action?.couriers)
            ? d.action.couriers.map((c) => ({
                ...c,
                id: c.id || uid("cour"),
              }))
            : [],
        },
      });
    } catch (e) {
      console.error(e);
      setError("Unable to load shipping rule");
    } finally {
      setLoading(false);
    }
  };

  const usedTypes = rule.conditions.map((c) => c.type);

  const addCondition = () => {
    if (rule.conditions.length >= MAX_CONDITIONS) return;
    const nextType =
      CONDITION_TYPES.find(([v]) => !usedTypes.includes(v))?.[0] || "payment";
    setRule((r) => ({
      ...r,
      conditions: [...r.conditions, emptyCondition(nextType)],
    }));
  };

  const updateCondition = (id, patch) => {
    setRule((r) => ({
      ...r,
      conditions: r.conditions.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      ),
    }));
  };

  const removeCondition = (id) => {
    setRule((r) => ({
      ...r,
      conditions: r.conditions.filter((c) => c.id !== id),
    }));
  };

  const addCourier = () => {
    if (rule.action.couriers.length >= 5) {
      return;
    }

    setRule((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        couriers: [
          ...prev.action.couriers,
          {
            id: uid(),
            product_id: "",
            product_name: "",
            priority: prev.action.couriers.length + 1,
          },
        ],
      },
    }));
  };

  const updateCourier = (id, patch) => {
    setRule((r) => ({
      ...r,
      action: {
        ...r.action,
        couriers: r.action.couriers.map((c) =>
          c.id === id ? { ...c, ...patch } : c
        ),
      },
    }));
  };

  const removeCourier = (id) => {
    setRule((r) => ({
      ...r,
      action: {
        ...r.action,
        couriers: r.action.couriers
          .filter((c) => c.id !== id)
          .map((c, i) => ({ ...c, priority: i + 1 })),
      },
    }));
  };

  const reorderCourier = (fromId, toId) => {
    const list = [...rule.action.couriers];
    const from = list.findIndex((x) => x.id === fromId);
    const to = list.findIndex((x) => x.id === toId);
    if (from < 0 || to < 0 || from === to) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setRule((r) => ({
      ...r,
      action: {
        ...r.action,
        couriers: list.map((c, i) => ({ ...c, priority: i + 1 })),
      },
    }));
  };

  const handleCourierChange = (id, changes) => {
    setRule((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        couriers: prev.action.couriers.map((item) =>
          item.id === id
            ? {
                ...item,
                ...changes,
              }
            : item
        ),
      },
    }));
  };

  const handleCourierRemove = (id) => {
    setRule((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        couriers: prev.action.couriers
          .filter((item) => item.id !== id)
          .map((item, index) => ({
            ...item,
            priority: index + 1,
          })),
      },
    }));
  };

  const handleCourierDrop = (fromId, toId) => {
    setRule((prev) => {
      const couriers = [...prev.action.couriers];

      const fromIndex = couriers.findIndex((item) => item.id === fromId);

      const toIndex = couriers.findIndex((item) => item.id === toId);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return prev;
      }

      const [movedItem] = couriers.splice(fromIndex, 1);

      couriers.splice(toIndex, 0, movedItem);

      const reorderedCouriers = couriers.map((item, index) => ({
        ...item,
        priority: index + 1,
      }));

      return {
        ...prev,
        action: {
          ...prev.action,
          couriers: reorderedCouriers,
        },
      };
    });
  };

  const handleFile = async (conditionId, key, file) => {
    if (!file) return;
    const allowed = [".csv", ".xlsx"];
    if (!allowed.some((x) => file.name.toLowerCase().endsWith(x))) {
      alert("Please upload CSV or XLSX file.");
      return;
    }

    const form = new FormData();
    form.append("customer_id", userData.customer_id);
    form.append("condition_id", conditionId);
    form.append("file_type", key === "skuFile" ? "sku" : "pincode");
    form.append("file", file);

    try {
      const res = await axios.post(
        config.apiUrl + "/shipping_rule/upload_condition_file",
        form,
        {
          headers: {
            ...config.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!res.data?.status) {
        alert(res.data?.message || "Unable to process file");
        return;
      }

      updateCondition(conditionId, {
        [key]: res.data.output,
      });
    } catch (e) {
      console.error(e);
      alert("Unable to upload file");
    }
  };

  const clearFile = (conditionId, key) => {
    updateCondition(conditionId, { [key]: null });
  };

  const validate = () => {
    if (!rule.name.trim()) {
      setError("Please enter a rule name");
      return false;
    }

    // Custom action must have at least one valid product
    if (rule.action.mode === "custom") {
      if (
        !Array.isArray(rule.action.couriers) ||
        rule.action.couriers.length === 0
      ) {
        setError("Please add at least one product.");
        return false;
      }

      const invalidProduct = rule.action.couriers.some((c) => !c.product_id);

      if (invalidProduct) {
        setError("Please select a valid product for all rows.");
        return false;
      }
    }

    for (const c of rule.conditions) {
      if (c.type === "weight") {
        if (c.wtOp === "btw" && (c.wtFrom === "" || c.wtTo === "")) {
          setError("Please enter both weight values.");
          return false;
        }
        if (c.wtOp !== "btw" && c.wtVal === "") {
          setError("Please enter weight value.");
          return false;
        }
      }

      if (c.type === "orderValue") {
        if (c.valOp === "btw" && (c.valFrom === "" || c.valTo === "")) {
          setError("Please enter both order value values.");
          return false;
        }
        if (c.valOp !== "btw" && c.valVal === "") {
          setError("Please enter order value.");
          return false;
        }
      }
    }

    setError("");
    return true;
  };

  const saveRule = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const payload = {
        customer_id: userData.customer_id,
        ...(isEdit ? { rule_id: rule.rule_id || editId } : {}),
        name: rule.name.trim(),
        type: rule.type,
        conditions: rule.conditions,
        action: {
          mode: rule.action.mode,

          couriers: (rule.action.couriers || []).map((c, i) => ({
            id: c.id,

            product_id: c.product_id,

            product_name: c.product_name || "",

            priority: i + 1,
          })),
        },
      };

      const res = await axios.post(
        config.apiUrl +
          (isEdit ? "/shipping_rule/update" : "/shipping_rule/create"),
        payload,
        { headers: config.headers }
      );

      if (res.data?.status) {
        window.location.href = "/shipping_rules";
      } else {
        setError(res.data?.message || "Unable to save rule");
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Unable to save rule");
    } finally {
      setSaving(false);
    }
  };

  const actionText =
    rule.action.mode === "fastest"
      ? "Fastest"
      : rule.action.mode === "cheapest"
      ? "Cheapest"
      : "Custom";

  if (loading) {
    return (
      <div>
        <Header>
          <div className="content">
            <div className="text-center py-5">
              <div className="spinner-border"></div>
            </div>
          </div>
        </Header>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header>
        <div className="content">
          <div className="ol-header">
            <div className="ol-hd">
              <a
                href="/shipping_rules"
                className="ol-back"
                title="Back to rules"
              >
                <i className="bi bi-arrow-left"></i>
              </a>
              <div>
                <div className="ol-title">
                  {isEdit ? "Edit Shipping Rule" : "Create Shipping Rule"}
                </div>
                <div className="ol-sub">
                  <span className="dot"></span> Automation &amp; Rules{" "}
                  <span
                    className="dot"
                    style={{ background: "var(--t4)" }}
                  ></span>{" "}
                  <a href="/shipping_rules" className="ol-link">
                    Shipping Rules
                  </a>
                </div>
              </div>
            </div>
            <div className="ol-actions">
              <a href="/shipping_rules" className="ol-btn ghost">
                Cancel
              </a>
              <button
                type="button"
                className="ol-btn primary"
                onClick={saveRule}
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <i className="bi bi-check2"></i>
                )}
                {saving ? "Saving..." : " Save Rule"}
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          <div className="sr-wrap sr-form-wrap">
            <div className="sr-layout">
              <aside className="sr-rail">
                <div className="card-base sr-rail-card">
                  <div className="sr-rail-title">Sections</div>
                  <nav className="sr-rail-nav">
                    <a href="#srSec1" className="sr-rail-item active">
                      <span className="dot"></span> Rule Information
                    </a>
                    <a href="#srSec2" className="sr-rail-item">
                      <span className="dot"></span> Shipment Conditions
                    </a>
                    <a href="#srSec3" className="sr-rail-item">
                      <span className="dot"></span> Rule Action
                    </a>
                  </nav>
                </div>

                <div className="card-base sr-summary-card">
                  <div className="sr-summary-title">Rule Summary</div>
                  <div className="sr-summary-row">
                    <span>Name</span>
                    <b>{rule.name || "—"}</b>
                  </div>
                  <div className="sr-summary-row">
                    <span>Type</span>
                    <b>{rule.type}</b>
                  </div>
                  <div className="sr-summary-row">
                    <span>Conditions</span>
                    <b>{rule.conditions.length}/7</b>
                  </div>
                  <div className="sr-summary-row">
                    <span>Action</span>
                    <b>{actionText}</b>
                  </div>
                  <div className="sr-summary-divider"></div>
                  <small className="sr-summary-hint">
                    Status and evaluation order are managed from the Rules list
                    — drag rows to reorder and use the per-row switch to
                    activate.
                  </small>
                </div>
              </aside>

              <main className="sr-main">
                <section className="card-base sr-section" id="srSec1">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-info-circle"></i> Rule Information
                      </h2>
                      <p className="oa-block-sub">
                        Give your shipping rule a clear name and choose the rule
                        type.
                      </p>
                    </div>
                  </div>

                  <div className="sr-grid">
                    <div className="ol-field sr-col-6">
                      <label>
                        Shipping Rule Name <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        value={rule.name}
                        onChange={(e) =>
                          setRule((r) => ({ ...r, name: e.target.value }))
                        }
                        placeholder="e.g. Weekend COD Hold or Metro Prepaid Priority"
                      />
                    </div>

                    <div className="ol-field sr-col-6">
                      <label>Shipping Rule Type</label>
                      <select
                        value={rule.type}
                        onChange={(e) =>
                          setRule((r) => ({ ...r, type: e.target.value }))
                        }
                      >
                        <option value="Domestic">Domestic</option>
                        <option value="International">International</option>
                        <option value="COD">COD</option>
                        <option value="Prepaid">Prepaid</option>
                        <option value="B2B">B2B</option>
                        <option value="Custom">Custom</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section className="card-base sr-section" id="srSec2">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-funnel"></i> Shipment Conditions
                      </h2>
                      <p className="oa-block-sub">
                        All conditions are combined with AND. Order must satisfy
                        every active condition.
                      </p>
                    </div>
                    <div className="sr-cond-head-actions">
                      <span className="sr-cond-count">
                        <span>{rule.conditions.length}</span> of 7 used
                      </span>
                    </div>
                  </div>

                  <div id="srCondList">
                    {rule.conditions.length === 0 ? (
                      <div className="sr-cond-empty">
                        No conditions added. This rule will match all orders.
                      </div>
                    ) : (
                      rule.conditions.map((c, index) => (
                        <ConditionCard
                          key={c.id}
                          condition={c}
                          index={index}
                          usedTypes={usedTypes}
                          onChange={updateCondition}
                          onRemove={removeCondition}
                          onFile={handleFile}
                          onClearFile={clearFile}
                        />
                      ))
                    )}
                  </div>

                  <div className="sr-add-foot">
                    <button
                      type="button"
                      className="ol-btn sr-add-btn"
                      onClick={addCondition}
                      disabled={rule.conditions.length >= MAX_CONDITIONS}
                    >
                      <i className="bi bi-plus-lg"></i> Add Condition
                    </button>
                    <p className="sr-inline-hint">
                      <i className="bi bi-info-circle"></i> You can add up to 7
                      conditions per rule. A rule with 0 conditions will match
                      all orders.
                    </p>
                  </div>
                </section>

                <section className="card-base sr-section" id="srSec3">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-truck"></i> Rule Action / Courier
                        Selection
                      </h2>
                      <p className="oa-block-sub">
                        Choose how couriers are selected when conditions match.
                      </p>
                    </div>
                  </div>

                  <div className="sr-modes">
                    <ModeCard
                      active={rule.action.mode === "custom"}
                      mode="custom"
                      icon="bi-list-check"
                      title="Custom"
                      description="Pick specific couriers and set your own priority order."
                      onClick={(mode) =>
                        setRule((r) => ({
                          ...r,
                          action: { ...r.action, mode },
                        }))
                      }
                    />
                    <ModeCard
                      active={rule.action.mode === "fastest"}
                      mode="fastest"
                      icon="bi-lightning-charge"
                      title="Fastest"
                      description="Prefer couriers that can deliver fastest within your constraints."
                      onClick={(mode) =>
                        setRule((r) => ({
                          ...r,
                          action: { ...r.action, mode },
                        }))
                      }
                    />
                    <ModeCard
                      active={rule.action.mode === "cheapest"}
                      mode="cheapest"
                      icon="bi-cash-coin"
                      title="Cheapest"
                      description="Prefer the lowest shipping cost couriers within your limits."
                      onClick={(mode) =>
                        setRule((r) => ({
                          ...r,
                          action: { ...r.action, mode },
                        }))
                      }
                    />
                  </div>

                  {rule.action.mode === "custom" && (
                    <div className="sr-block sr-courier-block" id="srCourBlock">
                      <div className="sr-courier-head">
                        <div>
                          <div className="sr-block-title">Couriers</div>
                          <small className="sr-toggle-sub">
                            Drag rows to set priority. Top is tried first.
                          </small>
                        </div>
                        <div className="sr-courier-actions">
                          <span className="sr-cond-count">
                            {rule.action.couriers.length} of 5
                          </span>
                        </div>
                      </div>

                      <div className="sr-courier-list" id="srCourList">
                        {rule.action.couriers.length === 0 ? (
                          <div className="sr-courier-empty">
                            No products added yet. Use “Add Product” to start
                            building your priority list.
                          </div>
                        ) : (
                          rule.action.couriers.map((c, i) => (
                            <CourierRow
                              key={c.id}
                              courier={c}
                              index={i}
                              dragId={dragId}
                              setDragId={setDragId}
                              onDrop={handleCourierDrop}
                              onChange={handleCourierChange}
                              onRemove={handleCourierRemove}
                              courierPartners={courierPartners}
                              loadingCourierPartners={loadingCourierPartners}
                              couriers={rule?.action?.couriers || []}
                            />
                          ))
                        )}
                      </div>

                      <div className="sr-add-foot">
                        <button
                          type="button"
                          className="ol-btn sr-add-btn"
                          onClick={addCourier}
                          disabled={rule.action.couriers.length >= MAX_COURIERS}
                        >
                          <i className="bi bi-plus-lg"></i> Add Courier
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </main>
            </div>
          </div>
        </div>
      </Header>
      <Footer />
    </div>
  );
};

const ModeCard = ({ active, mode, icon, title, description, onClick }) => (
  <button
    type="button"
    className={`ol-mode-card sr-mode ${active ? "on" : ""}`}
    onClick={() => onClick(mode)}
  >
    <span className="ol-mode-radio"></span>
    <span className="ol-mode-ic">
      <i className={`bi ${icon}`}></i>
    </span>
    <span className="ol-mode-tx">
      <span className="ol-mode-title">{title}</span>
      <span className="ol-mode-desc">{description}</span>
    </span>
  </button>
);

const CourierRow = ({
  courier,
  index,
  dragId,
  setDragId,
  onDrop,
  onChange,
  onRemove,
  courierPartners = [],
  loadingCourierPartners = false,
  couriers = [],
}) => {
  const selected = courierPartners.find(
    (item) => String(item.product_id) === String(courier.product_id)
  );

  return (
    <div
      className={`sr-courier-row oa-row-drag ${
        dragId === courier.id ? "sr-dragging" : ""
      }`}
      draggable
      data-id={courier.id}
      onDragStart={(e) => {
        setDragId(courier.id);

        e.dataTransfer.effectAllowed = "move";

        e.dataTransfer.setData("text/plain", courier.id);
      }}
      onDragOver={(e) => {
        e.preventDefault();

        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();

        const fromId = e.dataTransfer.getData("text/plain");

        if (fromId && fromId !== courier.id) {
          onDrop(fromId, courier.id);
        }

        setDragId(null);
      }}
      onDragEnd={() => {
        setDragId(null);
      }}
    >
      {/* Drag Handle */}
      <i className="bi bi-grip-vertical sr-grip"></i>

      {/* Priority / Rank */}
      <span className="sr-rank">{index + 1}</span>

      {/* Product Badge */}
      <span className="sr-cr-badge" data-role="badge">
        <span className="dapi-cl">
          {selected?.product_name?.charAt(0)?.toUpperCase() || "P"}
        </span>
      </span>

      <div className="sr-cr-fields">
        {/* Product Selection */}
        <div className="ol-field sr-cr-courier">
          <select
            value={courier.product_id || ""}
            disabled={loadingCourierPartners}
            onChange={(e) => {
              const productId = e.target.value;

              const product = courierPartners.find(
                (item) => String(item.product_id) === String(productId)
              );

              onChange(courier.id, {
                product_id: productId,

                product_name: product?.product_name || "",
              });
            }}
            aria-label="Shipping Product"
          >
            <option value="">
              {loadingCourierPartners
                ? "Loading products..."
                : "Select Product"}
            </option>

            {courierPartners.map((product) => {
              const alreadySelected = couriers.some(
                (item) =>
                  item.id !== courier.id &&
                  String(item.product_id) === String(product.product_id)
              );

              return (
                <option
                  key={product.product_id}
                  value={product.product_id}
                  disabled={alreadySelected}
                >
                  {product.product_name}
                  {alreadySelected ? " (Already selected)" : ""}
                </option>
              );
            })}
          </select>
        </div>

        {/* Selected Product Name */}
        <span className="sr-cr-service" data-role="service">
          {selected ? (
            <>
              <i className="bi bi-box-seam"></i> {selected.product_name}
            </>
          ) : (
            <span className="sr-muted">Select a product</span>
          )}
        </span>

        {/* Priority */}
        <div className="ol-field sr-cr-prio">
          <input
            type="number"
            min="1"
            step="1"
            value={courier.priority ?? index + 1}
            title="Priority"
            onChange={(e) => {
              const priority = Number(e.target.value);

              onChange(courier.id, {
                priority: priority > 0 ? priority : index + 1,
              });
            }}
          />
        </div>
      </div>

      {/* Remove Product */}
      <button
        type="button"
        className="sr-remove-cour"
        title="Remove product"
        onClick={() => onRemove(courier.id)}
      >
        <i className="bi bi-x-lg"></i>
      </button>
    </div>
  );
};

const ConditionCard = ({
  condition,
  index,
  usedTypes,
  onChange,
  onRemove,
  onFile,
  onClearFile,
}) => {
  const set = (patch) => onChange(condition.id, patch);

  const typeOptions = CONDITION_TYPES.map(([value, label]) => ({
    value,
    label,
    used: usedTypes.includes(value) && value !== condition.type,
  }));

  return (
    <div className="card-base sr-cond" data-id={condition.id}>
      <div className="sr-cond-head">
        <div className="sr-cond-left">
          <div className="ol-field sr-cond-type">
            <label>Condition Type</label>
            <select
              value={condition.type}
              onChange={(e) => set({ type: e.target.value })}
            >
              {typeOptions.map((x) => (
                <option key={x.value} value={x.value} disabled={x.used}>
                  {x.label}
                  {x.used ? " (in use)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className="sr-cond-remove"
          title="Remove condition"
          onClick={() => onRemove(condition.id)}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div className="sr-cond-body">
        {condition.type === "payment" && (
          <div className="sr-grid">
            <div className="sr-col-12">
              <div className="sr-inline-label">Payment Mode</div>
              <Segment
                current={condition.payment}
                options={[
                  ["Prepaid", "Prepaid"],
                  ["COD", "COD"],
                ]}
                onChange={(value) => set({ payment: value })}
              />
            </div>
          </div>
        )}

        {condition.type === "weight" && (
          <div className="sr-grid">
            <div className="sr-col-12">
              <div className="sr-inline-label">Weight Operator</div>
              <Segment
                current={condition.wtOp}
                options={[
                  ["lte", "Less Than Equal To"],
                  ["gte", "Greater Than Equal To"],
                  ["btw", "Between"],
                ]}
                onChange={(value) => set({ wtOp: value })}
              />
            </div>

            {condition.wtOp === "btw" ? (
              <>
                <Field
                  col="sr-col-3"
                  label="Weight From"
                  type="number"
                  value={condition.wtFrom}
                  onChange={(value) => set({ wtFrom: value })}
                />
                <Field
                  col="sr-col-3"
                  label="Weight To"
                  type="number"
                  value={condition.wtTo}
                  onChange={(value) => set({ wtTo: value })}
                />
              </>
            ) : (
              <Field
                col="sr-col-3"
                label="Weight Value"
                type="number"
                value={condition.wtVal}
                onChange={(value) => set({ wtVal: value })}
              />
            )}

            <div className="ol-field sr-col-3">
              <label>Weight Unit</label>
              <select
                value={condition.wtUnit}
                onChange={(e) => set({ wtUnit: e.target.value })}
              >
                <option value="KG">KG</option>
                {/* <option value="GM">GM</option> */}
              </select>
            </div>
          </div>
        )}

        {condition.type === "orderValue" && (
          <div className="sr-grid">
            <div className="sr-col-12">
              <div className="sr-inline-label">Order Value Operator</div>
              <Segment
                current={condition.valOp}
                options={[
                  ["lte", "Less Than Equal To"],
                  ["gte", "Greater Than Equal To"],
                  ["btw", "Between"],
                ]}
                onChange={(value) => set({ valOp: value })}
              />
            </div>

            {condition.valOp === "btw" ? (
              <>
                <Field
                  col="sr-col-3"
                  label="Order Value From"
                  type="number"
                  value={condition.valFrom}
                  onChange={(value) => set({ valFrom: value })}
                />
                <Field
                  col="sr-col-3"
                  label="Order Value To"
                  type="number"
                  value={condition.valTo}
                  onChange={(value) => set({ valTo: value })}
                />
              </>
            ) : (
              <Field
                col="sr-col-3"
                label="Order Value"
                type="number"
                value={condition.valVal}
                onChange={(value) => set({ valVal: value })}
              />
            )}

            {/* <div className="ol-field sr-col-3">
              <label>Order Value Currency</label>
              <select
                value={condition.valCur}
                onChange={(e) => set({ valCur: e.target.value })}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div> */}
          </div>
        )}

        {condition.type === "pincode" && (
          <div className="sr-upload-grid">
            <UploadBox
              label="Pickup Pincodes"
              data={condition.pickupFile}
              onFile={(file) => onFile(condition.id, "pickupFile", file)}
              onClear={() => onClearFile(condition.id, "pickupFile")}
            />
            <UploadBox
              label="Delivery Pincodes"
              data={condition.deliveryFile}
              onFile={(file) => onFile(condition.id, "deliveryFile", file)}
              onClear={() => onClearFile(condition.id, "deliveryFile")}
            />
          </div>
        )}

        {condition.type === "zone" && (
          <>
            <div className="sr-inline-label">Zone Wise</div>
            <div className="sr-zone-grid">
              {ZONES.map((zone) => {
                const checked = (condition.zones || []).includes(zone);
                return (
                  <label
                    key={zone}
                    className={`sr-zone ${checked ? "on" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(condition.zones || []), zone]
                          : (condition.zones || []).filter((x) => x !== zone);
                        set({ zones: next });
                      }}
                    />
                    <span className="box"></span>
                    <span className="lbl">{zone}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {condition.type === "state" && (
          <div className="sr-grid">
            <div className="ol-field sr-col-6">
              <label>From State</label>
              <select
                value={condition.fromState}
                onChange={(e) => set({ fromState: e.target.value })}
              >
                <option value="">Select State</option>
                {STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="ol-field sr-col-6">
              <label>To State</label>
              <select
                value={condition.toState}
                onChange={(e) => set({ toState: e.target.value })}
              >
                <option value="">Select State</option>
                {STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {condition.type === "sku" && (
          <div className="sr-upload-grid">
            <UploadBox
              label="Product SKUs"
              data={condition.skuFile}
              onFile={(file) => onFile(condition.id, "skuFile", file)}
              onClear={() => onClearFile(condition.id, "skuFile")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Segment = ({ current, options, onChange }) => (
  <div className="sr-seg">
    {options.map(([value, label]) => (
      <button
        type="button"
        key={value}
        className={current === value ? "on" : ""}
        onClick={() => onChange(value)}
      >
        {label}
      </button>
    ))}
  </div>
);

const Field = ({ col, label, type = "text", value, onChange }) => (
  <div className={`ol-field ${col}`}>
    <label>{label}</label>
    <input
      type={type}
      min={type === "number" ? 0 : undefined}
      step={type === "number" ? "0.1" : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const UploadBox = ({ label, data, onFile, onClear }) => {
  const inputId = uid("file");
  return (
    <div className={`sr-upload ${data ? "has-file" : ""}`}>
      <div className="sr-upload-top">
        <span className="sr-upload-label">{label}</span>
        <button
          type="button"
          className="sr-upload-btn"
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {data ? "Replace" : "Upload CSV / XLSX"}
        </button>
        <input
          id={inputId}
          type="file"
          accept=".csv,.xlsx"
          hidden
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>

      {data && (
        <div className="sr-file-info show">
          <i
            className="bi bi-file-earmark-check"
            style={{ color: "var(--green)" }}
          ></i>
          <span className="sr-file-name">{data.name}</span>
          <span>· {data.count} entries</span>
          {data.skipped ? (
            <span className="sr-pill sr-pill-warn">
              {data.skipped} invalid skipped
            </span>
          ) : null}
          <button
            type="button"
            className="sr-file-clear"
            onClick={onClear}
            title="Remove"
          >
            <i className="bi bi-x-circle-fill"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default Shipping_rule_edit;
